import { useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Plus } from 'lucide-react';
import { useChatStore } from '../../store/useChatStore';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import type { ChatInputHandle } from './ChatInput';
import { SetupPlanProgress } from './SetupPlanProgress';

export function ChatDrawer() {
  const isOpen = useChatStore((s) => s.isOpen);
  const chatMode = useChatStore((s) => s.chatMode);
  const close = useChatStore((s) => s.close);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const isLoading = useChatStore((s) => s.isLoading);
  const isLoadingConversation = useChatStore((s) => s.isLoadingConversation);
  const setupPlan = useChatStore((s) => s.setupPlan);
  const startNewConversation = useChatStore((s) => s.startNewConversation);
  const messages = useChatStore((s) => s.messages);
  const conversationId = useChatStore((s) => s.conversationId);
  const chatInputRef = useRef<ChatInputHandle>(null);

  // Close on Escape
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) close();
    },
    [isOpen, close]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSend = (content: string) => {
    sendMessage(content);
  };

  const showNewChat = conversationId && messages.length > 0;

  return (
    <AnimatePresence>
      {isOpen && chatMode === 'text' && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-[999]"
            onClick={close}
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.y > 100 || info.velocity.y > 500) {
                close();
              }
            }}
            data-testid="chat-drawer"
            className="fixed bottom-0 left-0 right-0 z-[1000] bg-card rounded-t-[28px] shadow-sheet flex flex-col max-w-2xl mx-auto"
            style={{ height: '85vh', maxHeight: '85vh' }}
            role="dialog"
            aria-label="Tidy AI Assistant"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-[5px] rounded-full bg-muted-foreground/30" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border">
              <div>
                <h2 className="text-lg font-extrabold tracking-tight text-foreground">Tidy AI</h2>
                <p className="text-xs text-muted-foreground">Your cleaning assistant</p>
              </div>
              <div className="flex items-center gap-1">
                {showNewChat && (
                  <button
                    onClick={startNewConversation}
                    className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="New chat"
                    title="New chat"
                  >
                    <Plus size={18} className="text-muted-foreground" />
                  </button>
                )}
                <button
                  onClick={close}
                  className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="Close chat"
                >
                  <X size={20} className="text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Setup plan progress bar */}
            {setupPlan && <SetupPlanProgress plan={setupPlan} />}

            {/* Messages */}
            {isLoadingConversation ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-6 h-6 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin" />
                  <p className="text-sm text-muted-foreground">Loading conversation...</p>
                </div>
              </div>
            ) : (
              <ChatMessageList onSuggestionSelect={handleSend} focusChatInput={() => chatInputRef.current?.focus()} />
            )}

            {/* Input */}
            <ChatInput ref={chatInputRef} onSend={handleSend} disabled={isLoading || isLoadingConversation} />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
